<script>
import { defineComponent, inject, ref, computed, onMounted, watch } from 'vue'
import axios from "axios"
import { anchorLockNote } from '@rm-ui/levelling/levellingMeta'

export default defineComponent({
  name: 'EvaluationInvite',
  components: {},
  props: {
    doc: {
      type: Object,
      required: false
    },
    title: {
      type: String,
      required: false,
      default: "Create a New Job Profile"
    },
    subTitle: {
      type: String,
      required: false,
      default: "Set the Level for This Role"
    },
    frameLess: {
      type: Boolean,
      required: false,
      default: false
    },
    /* Anchor roles (PD-1258). This screen is where "Evaluate as anchor role" actually
       lands, so the constraint is stated here rather than left to the disabled method
       cards one step later. The Holder owns the intent and passes it down. */
    anchorIntent: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Whether the escape hatch may be offered at all — a permission question. */
    canGovernAnchors: {
      type: Boolean,
      required: false,
      default: false
    },
    /** The role's family, for the "calibration reference for X" line. */
    anchorFamily: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: ['docCreated', 'error', 'sessionExpired', 'dropAnchorIntent'],
  setup(props, { emit }) {
    const loginState = inject('loginState')
    const store = inject('store')
    const router = inject('router');
    const viewState = ref('loading');
    const message = ref('');
    const response = ref(null);
    const workflowActions = ref([]);
    const evaluationChoice = ref(null)
    const dataCaptures = ref([]);
    const choiceValid = ref(false);
    /* Shared copy, so this screen and beta's gate cannot drift. Also supplies both
       choice-card descriptions, which change under the lock. */
    const anchorLock = computed(() => anchorLockNote({
      locked: props.anchorIntent,
      family: props.anchorFamily,
      canEscape: props.canGovernAnchors,
    }));
    const expandedInviteIndex = ref(null);

    onMounted(() => {
      viewState.value = 'loading';
      loadInviteAction();
    })

    const evaluateActions = computed(() => {
      return workflowActions.value.filter(a => !a.data_capture_method && !a.lifecycle_role_id_created);
    })

    function loadInviteAction() {
      axios
        .get(`workflow/getDocumentLevellingEvaluationActions/${props.doc.doc_id}`)
        .then(async (resp) => {
          if (resp.data.Status === "OK") {
            const actions = resp.data.Data;

            const inviteActions = actions.filter(a => a.lifecycle_role_id_created)
            if (inviteActions.length) {
              inviteActions.forEach(a => {
                doWorkflowAction(a)
              })
            }

            workflowActions.value = actions;

          } else {
            emit('error', resp.data.Message || 'Error creating document');
          }
          response.value = resp.data;
          viewState.value = 'invite'
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            console.log(err);
            emit('error', err.response?.data || 'Error creating document');
          }
        });
      
    }

    function doWorkflowAction(action, savedata, dcData) {
      //}, savedata, dcData) {
      if (!props.doc) return;
      let dc = {};
      let request = {
        doc_id: props.doc.doc_id,
        action_available_id: action.action_available_id,
        lifecycle_role_id: action.lifecycle_role_id,
        document_role_id: action.document_role_id,
        preProcessResponse: null,
        returnHeaderOnly: true,
      };
      let dcReadOnly = null;
      if (savedata) {
        if (dcData.inputs && dcData.inputs.length) {
          request.preProcessResponse = dcData;
        } else if (dcData.hr_id || dcData.hr_id_copy) {
          request.preProcessResponse = { ...dcData };
        } else if (action.data_capture_method === "review_group") {
          request.preProcessResponse = { newReviewGroupId: dcData };
        } else if (
          action.data_capture_method === "requisition" ||
          action.data_capture_method === "requisition_new"
        ) {
          request.preProcessResponse = { requisitions: dcData.requisitions };
        } else {
          request.preProcessResponse = dcData.value;
        }
      }

      return axios
        .post("document/processdocumentaction/", request)
        .then((resp) => {
          let result = { redirectURL: "" };
          if (resp.data.Status === "OK") {
            if (resp.data.Data.dataCapture) {
              dc.action = action;
              dc.action.definition = resp.data.Data.dataCapture;
              if (
                dc.action.isDataCaptureEditPopup ||
                dc.action.definition.lifecycle_role_id_created ||
                dc.action.definition.isCommentBased ||
                dc.action.definition.isCheckBased
              ) {
                
                if (dc.action.definition.lifecycle_role_id_created) {
                  dc.action.definition.users.userlist = dc.action.definition.users.userlist
                    .filter((u, i, arr) => arr.findIndex(o => o.user_id === u.user_id) === i)
                    .map(u => {
                      //u.text = `${u.user_name} <${u.email}>`;
                      u.text = u.user_name;
                      return u;
                    })
                    .sort((a, b) => {
                      let aname = a.user_name.toLowerCase();
                      let bname = b.user_name.toLowerCase();
                      return aname > bname ? 1 : aname < bname ? -1 : 0;
                    });
                  dc.action.definition.value = {
                    selectedUsers: dc.action.definition.multiple ? [] : "",
                    deletedUsers: [],
                    selectedReviewGroup: dc.action.definition.users.reviewGroupsLinked.find(
                      (x) => x.review_group_id > 0
                    )?.review_group_id,
                  };
                } else {
                  dc.action.definition.value = null;
                }
                dc.show = true;
                dataCaptures.value.push(dc);
              }
              return;
            } else {
              if (
                dcReadOnly &&
                dcReadOnly.definition &&
                dcReadOnly.definition.inputs?.length
              ) {
                dcReadOnly.definition.inputs.forEach((i) => {
                  let newVal = request.preProcessResponse.find(
                    (r) => r.check_type_id === i.check_type_id
                  );
                  if (newVal) i.value = newVal.value;
                });
              }
              dc.action = null;
              dc.show = false;
              if (resp.data.Data.documents && resp.data.Data.documents.length) {
                store.dispatch(
                  "docs/updateDoc",
                  resp.data.Data.documents[0]
                );
                store.dispatch(
                  "tasks/refresh",
                  resp.data.Data.documents[0].doc_id
                );
              }
            }
          } else {
            console.log(resp.data.Message);
          }
          return result;
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            console.log(err);
          }
          return false;
        });
    }

    function close(action) {
      // this.creationDisabled = true;
      // this.hierarchies.splice(0);
      emit("closed", action);
    }

    watch(evaluationChoice, () => {
      setUIstate(false);
    })

    function setUIstate(makeDirty) {
      let isDirty = false;
      let message = "";
      let isValid = false;
      let changesDescription = "";

      if (evaluationChoice.value.startsWith("evaluate") || evaluationChoice.value.startsWith("later")) {
        isValid = true;
      } else if (evaluationChoice.value.startsWith("invite")) {
        const dataCaptureIndex = parseInt(evaluationChoice.value.split("-")[1]);
        const dataCapture = dataCaptures.value[dataCaptureIndex];
        const action = dataCapture?.action;
        const definition = action?.definition;
        if (!definition)  {
          choiceValid.value = isValid;
          return;
        }

        if (makeDirty) isDirty = true;
        isValid =
          //!action.role_required ||
          definition.value.selectedReviewGroup ||
          definition.value.selectedUsers?.length > 0
        message = !isValid
          ? `This document must have a ${definition.data_capture_name}`
          : "";
        //this.UIstate.errorMessage = message;
        changesDescription = [
          {
            use: definition.value.deletedUsers.length > 0,
            message: `${definition.value.deletedUsers.length} ${definition.data_capture_name}${
              definition.value.deletedUsers.length > 1 ? "s" : ""
            } removed`,
          },
          {
            use: definition.value.selectedUsers?.length > 0,
            message: definition.multiple ?
              `${definition.value.selectedUsers?.length} ${definition.data_capture_name}${
                definition.value.selectedUsers?.length > 1 ? "s" : ""
              } assigned` : `${definition.data_capture_name} assigned`,
          },
        ]
          .filter((x) => x.use)
          .map((x) => x.message)
          .join(", ");
      }

      choiceValid.value = isValid;
    }

    // "I'll evaluate the level now" — run the evaluate action and go straight in.
    function chooseEvaluate(index) {
      const action = evaluateActions.value[index];
      if (!action) return;
      doWorkflowAction(action).then(() => {
        emit("start");
      });
    }

    // "Invite someone" — expand (or collapse) the evaluator picker inline.
    function toggleInvite(index) {
      expandedInviteIndex.value = expandedInviteIndex.value === index ? null : index;
      evaluationChoice.value = `invite-${index}`;
    }

    function sendInvitation(index) {
      if (!choiceValid.value) return;
      const dataCapture = dataCaptures.value[index];
      if (!dataCapture) return;
      doWorkflowAction(dataCapture.action, true, dataCapture.action.definition).then((result) => {
        if (result && result.redirectURL) {
          router.push(result.redirectURL);
        }
        const userList = dataCapture.action.definition.users.userlist;
        const selectedUsers = userList.filter((u) => dataCapture.action.definition.value.selectedUsers === u.user_id || dataCapture.action.definition.value.selectedUsers.includes(u.user_id));
        emit("start", selectedUsers);
      });
    }

    function chooseLater() {
      close('later');
    }

    return {
      props,
      loginState,
      viewState,
      message,
      close,
      evaluateActions,
      dataCaptures,
      evaluationChoice,
      choiceValid,
      anchorLock,
      expandedInviteIndex,
      chooseEvaluate,
      toggleInvite,
      sendInvitation,
      chooseLater,
      setUIstate,
    }
  }
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-inv" :class="frameLess ? 'lvl-qcard lvl-inv--card' : 'lvl-modal lvl-inv--framed'">
      <!-- Close control (framed / dialog only) -->
      <div v-if="!frameLess" class="lvl-inv-top">
        <button type="button" class="lvl-iconbtn" title="Close" @click="close(null)">
          <span class="ms">close</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="viewState === 'loading'" class="lvl-inv-loading">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <p>Please wait...</p>
      </div>

      <!-- Confirmation message -->
      <template v-else-if="viewState === 'message'">
        <div class="lvl-sent">
          <span class="lvl-sent-ic"><span class="ms fill">check</span></span>
          <h2>{{ message }}</h2>
          <button type="button" class="lvl-btn" @click="close(evaluationChoice)">Close</button>
        </div>
      </template>

      <!-- Invite / choose an evaluation method -->
      <template v-else-if="viewState === 'invite'">
        <div class="lvl-inv-body">
          <h2 class="lvl-q-title">{{ props.title }}</h2>
          <p class="lvl-q-desc">Choose how this role gets levelled. Score it yourself now, or invite a colleague to evaluate it and review their submission.</p>

          <div class="lvl-choices">
            <!-- Anchor lock (PD-1258). First child, as the prototype places it: the
                 constraint has to be read before the choice it constrains. -->
            <div v-if="anchorLock.locked" class="lvl-note lvl-note--info lvl-anchor-intronote">
              <v-icon size="17">mdi-anchor</v-icon>
              <span>
                <strong>{{ anchorLock.lead }}</strong> {{ anchorLock.body }}<template
                  v-if="anchorLock.canEscape"
                >{{ anchorLock.escapeLead }}<button
                  type="button"
                  class="lvl-anchor-route-act lvl-anchor-route-act--inline"
                  @click="$emit('dropAnchorIntent')"
                >{{ anchorLock.escapeLabel }}</button>.</template>
              </span>
            </div>

            <!-- Evaluate now → straight to the next step -->
            <button
              v-for="(workflowAction, index) in evaluateActions"
              :key="`evaluate-${index}`"
              type="button"
              class="lvl-choice"
              @click="chooseEvaluate(index)"
              :data-testid="'evaluate-option-' + workflowAction.name.replace(/\s+/g, '-').toLowerCase()"
            >
              <span class="lvl-choice-ic lvl-choice-ic--solid"><span class="ms" style="font-size:24px">bolt</span></span>
              <span class="lvl-choice-body">
                <span class="lvl-choice-t">{{ workflowAction.tooltip || workflowAction.name }}</span>
                <span class="lvl-choice-d">{{ anchorLock.nowDesc }}</span>
              </span>
              <span class="ms lvl-choice-go" style="font-size:20px">arrow_forward</span>
            </button>

            <!-- Invite someone → expands to the evaluator picker + Send -->
            <div
              v-for="(dataCapture, index) in dataCaptures"
              :key="`invite-${index}`"
              class="lvl-choice lvl-choice--col"
              :class="{ sel: expandedInviteIndex === index }"
            >
              <button type="button" class="lvl-choice-hit" @click="toggleInvite(index)">
                <span class="lvl-choice-ic"><span class="ms" style="font-size:24px">group_add</span></span>
                <span class="lvl-choice-body">
                  <span class="lvl-choice-t">Invite someone to evaluate</span>
                  <span class="lvl-choice-d">{{ anchorLock.inviteDesc }}</span>
                </span>
                <span class="ms lvl-choice-go" style="font-size:20px">{{ expandedInviteIndex === index ? 'expand_less' : 'expand_more' }}</span>
              </button>
              <div v-if="expandedInviteIndex === index" class="lvl-choice-expand">
                <div class="lvl-field">
                  <label>{{ 'Select ' + dataCapture.action.definition.users.role + (!!dataCapture.action.definition.multiple ? 's' : '') }}</label>
                </div>
                <v-autocomplete
                  class="lvl-inv-picker"
                  v-model="dataCapture.action.definition.value.selectedUsers"
                  placeholder="Search for a person"
                  :items="dataCapture.action.definition.users.userlist"
                  item-text="text"
                  item-value="user_id"
                  outlined
                  small-chips
                  dense
                  chips
                  clearable
                  deletable-chips
                  hide-details
                  :multiple="!!dataCapture.action.definition.multiple"
                  @input="setUIstate(true)"
                ></v-autocomplete>
                <div class="lvl-choice-send">
                  <button type="button" class="lvl-btn" :disabled="!choiceValid" @click="sendInvitation(index)">
                    Next: choose the method <span class="ms">send</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Do this later -->
            <button v-if="!frameLess" type="button" class="lvl-choice" @click="chooseLater()">
              <span class="lvl-choice-ic lvl-choice-ic--muted"><span class="ms" style="font-size:24px">schedule</span></span>
              <span class="lvl-choice-body">
                <span class="lvl-choice-t">Do this later</span>
                <span class="lvl-choice-d">You can return to it from the document view at any time.</span>
              </span>
              <span class="ms lvl-choice-go" style="font-size:20px">arrow_forward</span>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>
<style scoped lang="scss">
/* ------------------------------------------------------------------ *
 * EvaluationInvite: two shells over the same body.
 *  - frameLess (inline in the holder): white .lvl-qcard, centred by the
 *    holder — matches the Welcome screen.
 *  - framed (teleported v-dialog): .lvl-modal surface + close control.
 * ------------------------------------------------------------------ */

/* frameless: white card chrome (the .lvl-qcard supplies surface/border/shadow) */
.lvl-inv--card {
  width: 100%;
}

/* framed (dialog): .lvl-modal supplies surface/shadow/radius */
.lvl-inv--framed {
  position: relative;
}

.lvl-inv-top {
  display: flex;
  justify-content: flex-end;
  padding: 12px 12px 0;
}

.lvl-inv-body {
  padding: 4px 26px 6px;
}
.lvl-inv--framed .lvl-inv-body {
  padding-top: 0;
}
.lvl-inv--card .lvl-inv-body {
  padding: 26px 28px 30px;
}
.lvl-inv-body .lvl-q-desc {
  margin-bottom: 6px;
}

/* choice cards spacing */
.lvl-inv-body .lvl-choices {
  margin-top: 10px;
}

/* muted icon chip variant for the "Do this later" choice */
.lvl-choice-ic--muted {
  background: var(--v3-surface-2);
  color: var(--v3-text-3);
}

/* loading */
.lvl-inv-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 56px 24px;
  text-align: center;
}
.lvl-inv-loading p {
  margin: 0;
  font-size: 14px;
  color: var(--v3-text-2);
}

/* confirmation: centre the Close button under the success block */
.lvl-sent .lvl-btn {
  margin-top: 18px;
}

/* ---- reskin the real Vuetify controls to v3 tokens ---- */

/* selected radio / focused text-field accents (Vuetify defaults are
   accent/primary text colour classes) → brand teal, scoped here only */
::v-deep .primary--text,
::v-deep .accent--text {
  color: var(--v3-accent) !important;
  caret-color: var(--v3-accent) !important;
}

/* user picker (autocomplete, chips/multiple) — sits inside the expanded invite card */
.lvl-inv-picker {
  margin: 2px 0 2px;
  max-width: 440px;

  ::v-deep .v-input__slot {
    min-height: 42px;
  }
  ::v-deep fieldset {
    border-color: var(--v3-border);
    border-radius: var(--v3-r-sm);
  }
  &.v-input--is-focused ::v-deep fieldset {
    border-color: var(--v3-accent);
  }
  ::v-deep .v-label {
    font: 14px var(--font-sans);
    color: var(--v3-text-3);
  }
  ::v-deep input {
    font: 14px var(--font-sans);
    color: var(--v3-text);
  }
  ::v-deep .v-chip {
    background: color-mix(in srgb, var(--v3-accent) 10%, var(--v3-tint));
    color: var(--v3-accent);
    border-radius: 999px;
    font: 600 12.5px var(--font-sans);
  }
  ::v-deep .v-chip .v-icon {
    color: var(--v3-accent);
  }
}
</style>
